import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  useParams,
  withRouter,
} from 'react-router-dom';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import {
  Container,
  Button,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { fetchUserProfile } from '../../actions/Auth';

import {
  APIgetSettings,
  APIupdateSettings,
} from '../../api/Settings';
import { APIgetShareSettings } from '../../api/Shares';
import { APIgetUsers } from '../../api/User';

import SettingsSidebar from '../../components/Admin/Settings/Sidebar';
import SettingsContent from '../../components/Admin/Settings/Content';
import ErrorBoundary from '../../components/ErrorBoundary';

import { isMobile } from '../../helpers/App';
import { userCan } from '../../helpers/Auth';
import { formatSettings } from '../../helpers/DateTime';
import { addFlashMessage } from '../../helpers/FlashMessages';
import { getSettingsMap } from '../../helpers/SettingsMap';

import useForceUpdate from '../../hooks/useForceUpdate';

const AdminSettings = ({
  client, clientSettings, dispatch, profile,
}) => {
  const intl = useIntl();

  const settingsMap = getSettingsMap();

  const { tab, subTab } = useParams();
  const activeTab = tab || 'branding';
  const activeSubTab = subTab || null;

  const skipUnsavedCheckKeys = ['image_editor_presets'];

  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unsaved, setUnsaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedWorkspaceOwner, setSelectedWorkspaceOwner] = useState(client.workspace_owner);
  const [billingEmails, setBillingEmails] = useState(clientSettings.billing_emails);
  const [shareTemplates, setShareTemplates] = useState([]);
  const [selectedTemplateUuid, setSelectedTemplateUuid] = useState(null);
  const [errors, setErrors] = useState({});
  const [loadingAddTemplate, setLoadingAddTemplate] = useState(false);
  const [loadingRemoveTemplate, setLoadingRemoveTemplate] = useState(false);
  const [prefixAndSuffix, setPrefixAndSuffix] = useState(
    clientSettings.file_name_prefix_and_suffix,
  );
  const [navOpen, setNavOpen] = useState(!isMobile());

  const hasInitialized = useRef(false);

  const [openedCollapse, setOpenedCollapse] = useState(
    Object.fromEntries(settingsMap.map((s) => ([s.id, false]))),
  );

  const handleOpenNavbar = (id) => {
    setOpenedCollapse(prevOpenedCollapse => ({
      ...prevOpenedCollapse,
      [id]: !prevOpenedCollapse[id] ?? false,
    }));
  };

  const updateSettings = (settingsToUpdate) => {
    APIupdateSettings(settingsToUpdate).then(() => {
      dispatch(fetchUserProfile()).then(() => {
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'Changes have been saved' }),
        });
        setUnsaved(false);
        setSaving(false);
      });
    }).catch((error) => {
      setSaving(false);
      setErrors(error.response.data.errors);
    }).finally(() => {
      setLoadingAddTemplate(false);
      setLoadingRemoveTemplate(false);
    });
  };

  const handleSaveSettings = () => {
    setErrors({});
    setSaving(true);

    const settingsToUpdate = {};
    Object.keys(settings).forEach((key) => {
      settingsToUpdate[key] = settings[key].value;
    });

    if (selectedWorkspaceOwner) {
      settingsToUpdate.workspace_owner = selectedWorkspaceOwner.uuid;
    }
    if (billingEmails) {
      settingsToUpdate.billing_emails = billingEmails;
    }
    if (shareTemplates) {
      const newShareTemplates = shareTemplates.map(p => {
        if (p.touched) {
          const uuid = uuidv4();
          if (selectedTemplateUuid === p.uuid) {
            setSelectedTemplateUuid(uuid);
          }
          return {
            ...p,
            uuid,
          };
        }
        return p;
      });
      setShareTemplates(newShareTemplates);
      settingsToUpdate.share_templates = newShareTemplates;
    }
    if (prefixAndSuffix) {
      settingsToUpdate.file_name_prefix_and_suffix = prefixAndSuffix;
    }

    updateSettings(settingsToUpdate);
  };

  const forceUpdate = useForceUpdate();

  const loadUsers = () => {
    setUsersLoading(true);

    APIgetUsers({ role: 'admin' }).then((response) => {
      const { data } = response;
      if (data) {
        setUsers(data);
      }
      setUsersLoading(false);
    });
  };

  const handleSettingChange = (key, value) => {
    if (settings[key]) {
      settings[key].value = value;
      if (!skipUnsavedCheckKeys.includes(key)) {
        setUnsaved(true);
      }
      setSettings(settings);
      forceUpdate();
    }
  };

  const handleGetShareSettings = async (type) => {
    try {
      const response = await APIgetShareSettings({ type });
      setShareTemplates(response.data.share_templates);
    } catch (e) {
      setShareTemplates([]);
    }
  };

  const hiddenSettings = [];
  Object.keys(formatSettings(settings, false, intl)).forEach((key) => {
    if (settings[key].show_when_checked && !settings[settings[key].show_when_checked].value) {
      hiddenSettings.push(key);
    }
  });

  const handleShareTemplatesAdd = (name) => {
    const newUuid = uuidv4();

    setLoadingAddTemplate(true);
    setSelectedTemplateUuid(newUuid);

    const newTemplates = [...shareTemplates, { uuid: newUuid, name }];

    setShareTemplates(newTemplates);
    updateSettings({ share_templates: newTemplates });
  };

  const handleShareTemplatesChange = (uuid, settingKey, value) => {
    setUnsaved(true);
    setShareTemplates(prevTemplates => prevTemplates.map(p => {
      const newTemplate = { ...p, touched: true };

      if (p.uuid === uuid) {
        if (settingKey === 'file_sizes') {
          const fileSizes = newTemplate.file_sizes || [];
          if (value.checked) {
            newTemplate[settingKey] = [...fileSizes, `${value.value}`];
          } else if (newTemplate.file_sizes.length > 1) {
            newTemplate[settingKey] = fileSizes.filter(sfs => (sfs !== `${value.value}`));
          }
        } else if (settingKey === 'logo') {
          newTemplate[settingKey] = value;
          if (value === null) {
            newTemplate.logo_url = null;
          }
        } else if (settingKey === 'logo_url') {
          newTemplate[settingKey] = value;
        } else if (settingKey === 'hide_file_details') {
          newTemplate[settingKey] = value;
          if (value) {
            newTemplate.allow_comments = false;
          }
        } else {
          newTemplate[settingKey] = value;
        }
      }
      return newTemplate;
    }));
  };

  const handleShareTemplatesRemove = (uuid) => {
    const newTemplates = shareTemplates.filter(p => p.uuid !== uuid);

    setSelectedTemplateUuid(shareTemplates.length > 0 ? shareTemplates[0].uuid : null);
    setLoadingRemoveTemplate(true);
    setShareTemplates(newTemplates);
    updateSettings({ share_templates: newTemplates });
  };

  const handleSelectedTemplateUuid = (uuid) => {
    setSelectedTemplateUuid(uuid);
  };

  const handleSelectedWorkspaceOwner = (owner) => {
    setSelectedWorkspaceOwner(owner);
    setUnsaved(true);
  };

  const handleBillingEmails = (emails) => {
    setBillingEmails(emails);
    setUnsaved(true);
  };

  const handlePrefixAndSuffix = (type, segment, value) => {
    const prefixSuffixSetting = { ...prefixAndSuffix };

    prefixSuffixSetting[type][segment] = value;

    setPrefixAndSuffix(prefixSuffixSetting);
    setUnsaved(true);
  };

  useEffect(() => {
    if (hasInitialized.current) return;

    setOpenedCollapse(prev => {
      const newCollapseEntries = settingsMap.map((s) => {
        const indexes = s.items.map(item => item.id);
        const shouldOpen = indexes.includes(activeTab);

        return [s.id, shouldOpen || prev[s.id]];
      });

      const updated = Object.fromEntries(newCollapseEntries);
      return _.isEqual(prev, updated) ? prev : updated;
    });

    hasInitialized.current = true;
  }, [activeTab, settingsMap]);

  useEffect(() => {
    APIgetSettings().then((response) => {
      if (response && response.data) {
        setSettings(response.data);
      }
      setLoading(false);
    });
    loadUsers();
  }, []);

  useEffect(() => {
    handleGetShareSettings('workspace');
  }, []);

  const toggleNavOpen = () => {
    if (isMobile()) {
      setNavOpen(!navOpen);
    }
  };

  if (!profile) return null;

  if (!userCan('manage_settings') && userCan('add_users')) {
    if (activeTab !== 'users') {
      return null;
    }
  }

  return (
    <ErrorBoundary>
      <div className="main-content">
        <Container fluid className="settings-container">
          <div className="settings-menu-toggler-wrapper">
            <Button color="link" onClick={toggleNavOpen}>
              <i className="fas fa-bars" />
            </Button>
          </div>
          <div className="settings-content">
            {navOpen && (
              <SettingsSidebar
                openedCollapse={openedCollapse}
                handleOpen={handleOpenNavbar}
                activeTab={activeTab}
                toggleNavOpen={toggleNavOpen}
              />
            )}
            <SettingsContent
              handleSave={handleSaveSettings}
              activeTab={activeTab}
              activeSubTab={activeSubTab}
              selectedWorkspaceOwner={selectedWorkspaceOwner}
              handleSelectedWorkspaceOwner={handleSelectedWorkspaceOwner}
              handleSettingChange={handleSettingChange}
              users={users}
              settings={settings}
              hiddenSettings={hiddenSettings}
              errors={errors}
              setErrors={setErrors}
              profile={profile}
              saving={saving}
              unsaved={unsaved}
              handleShareTemplatesAdd={handleShareTemplatesAdd}
              handleShareTemplatesChange={handleShareTemplatesChange}
              handleShareTemplatesRemove={handleShareTemplatesRemove}
              handleSelectedTemplateUuid={handleSelectedTemplateUuid}
              loadingAddTemplate={loadingAddTemplate}
              loadingRemoveTemplate={loadingRemoveTemplate}
              selectedTemplateUuid={selectedTemplateUuid}
              shareTemplates={shareTemplates}
              billingEmails={billingEmails}
              handleBillingEmails={handleBillingEmails}
              handlePrefixAndSuffix={handlePrefixAndSuffix}
              usersLoading={usersLoading}
              loading={loading}
            />
          </div>
        </Container>
      </div>
    </ErrorBoundary>
  );
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
  client: state.Auth.profile?.client,
  clientSettings: state.App.settings,
});

AdminSettings.propTypes = {
  profile: PropTypes.object,
  client: PropTypes.object.isRequired,
  clientSettings: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

AdminSettings.defaultProps = {
  profile: null,
};

export default withRouter(connect(mapStateToProps)(AdminSettings));
